/**
 * Поля, которых нет в дизайн-системе: дата и денежная сумма.
 * Собраны из токенов в том же стиле, что Input (docs/10-design.md).
 */
import { useState, type ReactNode } from 'react';
import { Input } from '@/ds';
import { useControlSize } from '@/lib/useControlSize';

const CONTROL_HEIGHT = {
  sm: 'var(--control-h-sm)',
  md: 'var(--control-h-md)',
  lg: 'var(--control-h-lg)',
} as const;

function FieldShell({
  label,
  help,
  error,
  children,
}: {
  label?: string;
  help?: string;
  error?: string | null;
  children: ReactNode;
}) {
  return (
    <label
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-3)',
        width: '100%',
      }}
    >
      {label ? (
        <span
          style={{
            fontFamily: 'var(--font-text)',
            fontSize: 'var(--fs-xs)',
            fontWeight: 'var(--fw-medium)',
            color: 'var(--text-body)',
          }}
        >
          {label}
        </span>
      ) : null}
      {children}
      {error ? (
        <span
          style={{
            fontFamily: 'var(--font-text)',
            fontSize: 'var(--fs-2xs)',
            color: 'var(--status-danger-fg)',
          }}
        >
          {error}
        </span>
      ) : help ? (
        <span
          style={{
            fontFamily: 'var(--font-text)',
            fontSize: 'var(--fs-2xs)',
            color: 'var(--text-subtle)',
          }}
        >
          {help}
        </span>
      ) : null}
    </label>
  );
}

export function DateField({
  label,
  value,
  onChange,
  help,
  error,
}: {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  help?: string;
  error?: string | null;
}) {
  const [focus, setFocus] = useState(false);
  const size = useControlSize();
  const bad = Boolean(error);

  return (
    <FieldShell
      {...(label !== undefined ? { label } : {})}
      {...(help !== undefined ? { help } : {})}
      {...(error !== undefined ? { error } : {})}
    >
      <input
        type="date"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        style={{
          height: CONTROL_HEIGHT[size],
          padding: '0 var(--space-5)',
          width: '100%',
          background: 'var(--white)',
          border: `1px solid ${
            bad ? 'var(--red-600)' : focus ? 'var(--burgundy-700)' : 'var(--border-default)'
          }`,
          borderRadius: 'var(--radius-field)',
          boxShadow: focus ? (bad ? 'var(--ring-danger)' : 'var(--ring-brand)') : 'none',
          outline: 'none',
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--fs-sm)',
          color: 'var(--text-strong)',
          transition: 'var(--transition-control)',
        }}
      />
    </FieldShell>
  );
}

/**
 * Денежная сумма. Наружу отдаётся строка как её набрал человек, разбор
 * в минорные единицы делает вызывающая сторона через parseMinor.
 */
export function MoneyField({
  label,
  value,
  onChange,
  currencyHint,
  help,
  error,
  autoFocus,
}: {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  /** Подпись валюты справа от подсказки: «в долларах», «в рублях». */
  currencyHint?: string;
  help?: string;
  error?: string | null;
  autoFocus?: boolean;
}) {
  const size = useControlSize();
  const hint = [help, currencyHint].filter(Boolean).join(' · ');

  return (
    <Input
      size={size}
      {...(label !== undefined ? { label } : {})}
      type="text"
      placeholder="0,00"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      {...(hint ? { help: hint } : {})}
      {...(error ? { error } : {})}
      {...(autoFocus ? { autoFocus: true } : {})}
      style={{ fontFamily: 'var(--font-mono)' }}
    />
  );
}
